// Streams chapter audio from the backend's Range-capable proxy into local
// storage, one chapter at a time, resuming from the bytes already written.
import { getApiUrl } from '../services/apiConfig';
import { getStorage, chapterPath, bookDir } from './storage';
import { chapterAudioKeyCandidates } from './keys';

const FLUSH_BYTES = 2 * 1024 * 1024;

class WakeLock {
  constructor() { this.sentinel = null; this.onVisible = null; }
  async acquire() {
    if (typeof navigator === 'undefined' || !navigator.wakeLock) return;
    const request = async () => {
      try { this.sentinel = await navigator.wakeLock.request('screen'); } catch (e) { this.sentinel = null; }
      if (this.sentinel) this.sentinel.addEventListener('release', () => { this.sentinel = null; });
    };
    await request();
    this.onVisible = () => { if (document.visibilityState === 'visible' && !this.sentinel) request(); };
    document.addEventListener('visibilitychange', this.onVisible);
  }
  release() {
    if (this.onVisible) document.removeEventListener('visibilitychange', this.onVisible);
    if (this.sentinel) { this.sentinel.release().catch(() => {}); this.sentinel = null; }
  }
}

function parseTotal(response) {
  const range = response.headers.get('Content-Range');
  if (range) {
    const match = /\/(\d+)\s*$/.exec(range);
    if (match) return Number(match[1]);
  }
  return Number(response.headers.get('Content-Length') || 0);
}

// Read chapters straight from the bucket with a signed URL, so the bytes skip
// the API server (its outbound bandwidth is billed). Needs the bucket's CORS
// rule; the first failure switches this page load back to the proxied route.
let directOk = true;

async function openStream(key, offset, token, signal) {
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const range = offset > 0 ? { Range: `bytes=${offset}-` } : {};
  if (directOk) {
    try {
      const signed = await fetch(getApiUrl(`/api/wasabi/file/${encodeURIComponent(key)}?signed=1`), { headers, signal, credentials: 'include' });
      if (!signed.ok) return signed;
      const { url } = await signed.json();
      if (url) return await fetch(url, { headers: range, signal });
    } catch (error) {
      if (signal?.aborted) throw error;
      directOk = false;
    }
  }
  const url = getApiUrl(`/api/wasabi/file/${encodeURIComponent(key)}`);
  return fetch(url, { headers: { ...headers, ...range }, signal, credentials: 'include' });
}

/**
 * Download one chapter. Returns { key, bytes } on success.
 * onProgress({ bytes, total }) fires as data lands.
 */
export async function downloadChapter({ book, chapter, bookKey, userId, getToken, onProgress, signal }) {
  const storage = getStorage();
  const path = chapterPath(bookKey, chapter.id);
  const candidates = chapter.resolvedKey ? [chapter.resolvedKey] : chapterAudioKeyCandidates(book, chapter, userId);
  let lastError = null;

  for (const key of candidates) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    let written = await storage.size(path);
    let token = book.isCurated ? '' : await getToken();
    let response;
    try {
      response = await openStream(key, written, token, signal);
    } catch (error) {
      if (signal?.aborted) throw error;
      lastError = error;
      continue;
    }
    if (response.status === 401 && !book.isCurated) {
      token = await getToken(true);
      response = await openStream(key, written, token, signal);
    }
    if (response.status === 404 || response.status === 403) {
      lastError = new Error(`HTTP ${response.status}`);
      continue;
    }
    if (response.status === 416) {
      // Range starts at or past the end: everything is already on disk.
      const bytes = await storage.size(path);
      await storage.close(path);
      onProgress && onProgress({ bytes, total: bytes });
      return { key, bytes };
    }
    if (response.status === 200 && written > 0) {
      // Server ignored the Range header: start the file over.
      await storage.truncate(path, 0);
      written = 0;
    }
    if (!response.ok || !response.body) {
      lastError = new Error(`HTTP ${response.status}`);
      continue;
    }

    const expectedTotal = parseTotal(response) || 0;
    const reader = response.body.getReader();
    let buffered = [];
    let bufferedBytes = 0;

    const flush = async () => {
      if (!bufferedBytes) return;
      const merged = new Uint8Array(bufferedBytes);
      let at = 0;
      for (const part of buffered) { merged.set(part, at); at += part.length; }
      buffered = [];
      bufferedBytes = 0;
      // Read the length before the write: the buffer is transferred to the
      // OPFS worker and detached, after which merged.length reads as 0.
      const length = merged.length;
      await storage.writeChunk(path, written, merged.buffer);
      written += length;
      onProgress && onProgress({ bytes: written, total: expectedTotal });
    };

    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffered.push(value);
        bufferedBytes += value.length;
        if (bufferedBytes >= FLUSH_BYTES) await flush();
      }
      await flush();
    } catch (error) {
      await flush().catch(() => {});
      await storage.close(path);
      throw error;
    }
    await storage.close(path);
    if (expectedTotal && written < expectedTotal) {
      throw new Error(`Short read: ${written} of ${expectedTotal} bytes`);
    }
    return { key, bytes: written };
  }
  throw lastError || new Error('No audio found for this chapter');
}

/**
 * Download every chapter of a book sequentially. `record.chapters` entries
 * are mutated as bytes land so the caller can persist partial progress.
 */
export async function downloadBook({ record, book, userId, getToken, onChapterProgress, onChapterDone, signal }) {
  const lock = new WakeLock();
  await lock.acquire();
  try {
    for (let index = 0; index < record.chapters.length; index += 1) {
      const entry = record.chapters[index];
      if (entry.done) continue;
      const chapter = (book.chapters || [])[index] || { id: entry.id };
      const result = await downloadChapter({
        book,
        chapter: { ...chapter, id: entry.id, resolvedKey: entry.key || '' },
        bookKey: record.key,
        userId,
        getToken,
        signal,
        onProgress: ({ bytes, total }) => {
          entry.bytes = bytes;
          if (total) entry.total = total;
          onChapterProgress && onChapterProgress(index, entry);
        }
      });
      entry.key = result.key;
      entry.bytes = result.bytes;
      entry.total = result.bytes;
      entry.done = true;
      onChapterDone && onChapterDone(index, entry);
    }
  } finally {
    lock.release();
  }
}

export async function removeBookFiles(bookKey) {
  await getStorage().removeDir(bookDir(bookKey));
}

export async function getChapterFile(bookKey, chapterId) {
  return getStorage().getFile(chapterPath(bookKey, chapterId));
}
